/**
 * IndexedDB-backed photo storage.
 * Stores photo blobs outside localStorage to avoid the ~5MB limit.
 * Photos are referenced in CompletionRecord.photoUrl as "idb::{id}".
 */

const DB_NAME = 'homeschool-photos';
const STORE_NAME = 'photos';
const DB_VERSION = 1;

export const IDB_PREFIX = 'idb::';

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/** Save a base64 data URL to IndexedDB under the given key. */
export async function savePhoto(id: string, dataUrl: string): Promise<string> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).put(dataUrl, id);
    tx.oncomplete = () => resolve(`${IDB_PREFIX}${id}`);
    tx.onerror = () => reject(tx.error);
  });
}

/** Retrieve a base64 data URL from IndexedDB. Returns null if not found. */
export async function getPhoto(id: string): Promise<string | null> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const req = tx.objectStore(STORE_NAME).get(id);
    req.onsuccess = () => resolve((req.result as string) ?? null);
    req.onerror = () => reject(req.error);
  });
}

/** Delete a single photo by key. */
export async function deletePhoto(id: string): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).delete(id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/** Delete all photos for a set of keys. */
export async function deletePhotos(ids: string[]): Promise<void> {
  if (ids.length === 0) return;
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    for (const id of ids) {
      store.delete(id);
    }
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/** Delete all stored photos. Used when switching authenticated families on one device. */
export async function clearAllPhotos(): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).clear();
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/** Extract the IndexedDB key from an idb:: prefixed photoUrl. */
export function photoKey(url: string): string {
  return url.startsWith(IDB_PREFIX) ? url.slice(IDB_PREFIX.length) : url;
}

/** Check if a photoUrl is an IndexedDB reference. */
export function isIdbPhoto(url: string): boolean {
  return url.startsWith(IDB_PREFIX);
}
